import { useRef, useState } from "react";
import type { FormEvent } from "react";

const CONTACT_COOLDOWN_MS = 45_000;

type ContactStatus = "idle" | "submitting" | "success" | "error" | "cooldown";

function getLastContactTime() {
  try {
    return Number(window.localStorage.getItem("helloMarkyLastContact") || 0);
  } catch {
    return 0;
  }
}

export function ContactPage() {
  const [status, setStatus] = useState<ContactStatus>("idle");
  const [error, setError] = useState("");
  const [formStartedAt] = useState(() => Date.now());
  const submitLock = useRef(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitLock.current || status === "submitting" || status === "success") return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    if (formData.get("website")) return;
    if (Date.now() - Number(formData.get("formStartedAt")) < 1200) {
      setError("Take a second to add a little context, then try again.");
      setStatus("error");
      return;
    }
    if (Date.now() - getLastContactTime() < CONTACT_COOLDOWN_MS) {
      setStatus("cooldown");
      setError("We already received a note from this browser. Give us a moment before sending another.");
      return;
    }

    submitLock.current = true;
    setStatus("submitting");
    setError("");
    const payload = Object.fromEntries(formData.entries());
    delete payload.website;
    payload.source = "hello-marky-site";
    payload.submittedAt = new Date().toISOString();
    payload.submissionId = typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Contact request failed");
      window.localStorage.setItem("helloMarkyLastContact", String(Date.now()));
      setStatus("success");
      form.reset();
    } catch {
      submitLock.current = false;
      setStatus("error");
      setError("That note did not send. Please try again in a moment.");
    }
  }

  return (
    <main id="main-content" className="interior-world contact-world">
      <section className="contact-briefing">
        <div className="contact-briefing-copy">
          <p className="route-kicker" data-hero-support>A useful first conversation</p>
          <h1>
            <span data-hero-line><span>Bring us the</span></span>
            <span data-hero-line><span>heavy part.</span></span>
          </h1>
          <p data-hero-support>Start with the repeated task, awkward handoff, or customer moment consuming too much attention.</p>
          <div className="contact-direct" data-hero-support>
            <span>Prefer to email?</span>
            <a href="mailto:hi@hellomarky.com">hi@hellomarky.com</a>
          </div>
        </div>
        <div className="contact-form-stage" data-hero-visual>
          {status === "success" ? (
            <div className="contact-success" role="status">
              <img src="/brand/marky-solid.svg" alt="" aria-hidden="true" />
              <h2>Message received.</h2>
              <p>We will read the workflow, find the useful next question, and get back to you.</p>
            </div>
          ) : (
            <form className="contact-form contact-form--world" onSubmit={handleSubmit}>
              <div className="contact-form-heading">
                <h2>Start with what is real.</h2>
                <p>You do not need a technical brief. Tell us where the day keeps getting stuck.</p>
              </div>
              <div className="contact-form-row">
                <label><span>Name</span><input name="name" autoComplete="name" required /></label>
                <label><span>Email</span><input name="email" type="email" autoComplete="email" required /></label>
              </div>
              <label><span>Business <small>optional</small></span><input name="company" autoComplete="organization" /></label>
              <label><span>What would you like to make easier?</span><textarea name="message" rows={6} required /></label>
              <input className="honeypot" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <input type="hidden" name="formStartedAt" value={formStartedAt} />
              {error && <p className="contact-error" role="alert">{error}</p>}
              <div className="contact-form-footer">
                <small>We only use these details to reply to your enquiry.</small>
                <button className="world-button" type="submit" disabled={status === "submitting" || status === "cooldown"} aria-busy={status === "submitting"}>
                  {status === "submitting" ? "Sending..." : status === "cooldown" ? "Already received" : "Send the brief"} <span aria-hidden="true">↗</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
